// Registers the JobQ service worker (built by scripts/build-service-worker.js) and reports new
// builds to utils/swUpdate.js, which drives the "New version, tap to reload" banner.
//
// Production builds only (npm start has no worker). Not on the customer / vendor portals: a
// customer opening a link once should not download the whole app into a cache.
//
// "New build" means a worker holding a DIFFERENT build than this page runs: every worker of ours
// answers GET_BUILD with its main bundle. Anything else that lands on the same scope is not an
// update: OneSignal's worker (it still installs itself at "/" on browsers that allowed
// notifications; it never answers), or ours re-installed with the build this page already runs
// (that one simply takes over, silently).

const SW_URL = '/service-worker.js';
const NO_WORKER_PREFIXES = ['/approve/', '/component-ack/', '/tax-certificate/', '/sign/'];
// Phones keep the home-screen app open for days: look for a new build when it comes back to
// the front (at most every 10 min) and once an hour while it stays open.
const RECHECK_ON_SHOW_MS = 10 * 60 * 1000;
const RECHECK_EVERY_MS = 60 * 60 * 1000;
const ASK_BUILD_MS = 2000;

export function shouldRegister(pathname, env = process.env.NODE_ENV) {
  if (env !== 'production') return false;
  return !NO_WORKER_PREFIXES.some((p) => String(pathname || '').startsWith(p));
}

/** This page's build ("/static/js/main.<hash>.js"), or null (dev server). */
export function pageBuild(doc = document) {
  try {
    const s = Array.from(doc.scripts || []).find((el) => /\/static\/js\/main\.[0-9a-f]+\.js$/.test(el.src || ''));
    return s ? new URL(s.src).pathname : null;
  } catch (e) {
    return null;
  }
}

/** Ask a worker which build it serves. null = not ours / no answer in time. */
export function askWorkerBuild(worker, timeoutMs = ASK_BUILD_MS) {
  return new Promise((resolve) => {
    if (!worker || typeof MessageChannel === 'undefined') { resolve(null); return; }
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(null), timeoutMs);
    channel.port1.onmessage = (e) => {
      clearTimeout(timer);
      resolve((e.data && typeof e.data.build === 'string') ? e.data.build : null);
    };
    try {
      worker.postMessage({ type: 'GET_BUILD' }, [channel.port2]);
    } catch (e) {
      clearTimeout(timer);
      resolve(null);
    }
  });
}

/**
 * A worker is waiting behind this page's controller:
 *   'update'   it holds a different build -> show the banner
 *   'activate' it holds this page's build -> let it take over silently (nothing changes)
 *   'ignore'   not ours (OneSignal's) -> leave it alone
 */
export function waitingDecision({ waitingBuild, currentBuild }) {
  if (!waitingBuild) return 'ignore';
  if (currentBuild && waitingBuild === currentBuild) return 'activate';
  return 'update';
}

/** The controller changed: 'changed' only when the new one is ours with another build. */
export function controllerDecision({ controllerBuild, currentBuild }) {
  if (!controllerBuild) return 'ignore';
  if (currentBuild && controllerBuild === currentBuild) return 'ignore';
  return 'changed';
}

/**
 * @param {{onUpdate?: (reg: ServiceWorkerRegistration) => void, onControllerChange?: () => void}} config
 *   onUpdate: a new build is installed and waiting. onControllerChange: a worker with another
 *   build took over a page that already had one (never on a first install).
 */
export function register(config = {}) {
  if (!('serviceWorker' in navigator)) return;
  if (!shouldRegister(window.location.pathname)) return;

  // Captured now: a first install also changes the controller (clientsClaim), and that is not
  // an update.
  const hadController = Boolean(navigator.serviceWorker.controller);
  navigator.serviceWorker.addEventListener('controllerchange', async () => {
    if (!hadController || !config.onControllerChange) return;
    const controllerBuild = await askWorkerBuild(navigator.serviceWorker.controller);
    if (controllerDecision({ controllerBuild, currentBuild: pageBuild() }) === 'changed') {
      config.onControllerChange();
    }
  });

  const start = () => {
    navigator.serviceWorker
      .register(SW_URL)
      .then((registration) => watch(registration, config))
      .catch((error) => {
        console.error('Service worker registration failed:', error);
      });
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start);
}

function watch(registration, config) {
  const report = async () => {
    const waiting = registration.waiting;
    if (!waiting || !navigator.serviceWorker.controller) return;
    const decision = waitingDecision({ waitingBuild: await askWorkerBuild(waiting), currentBuild: pageBuild() });
    if (decision === 'activate') {
      try { waiting.postMessage({ type: 'SKIP_WAITING' }); } catch (e) { /* gone already */ }
    } else if (decision === 'update' && config.onUpdate) {
      config.onUpdate(registration);
    }
  };
  // A build installed while this page was closed is already waiting.
  report();
  registration.addEventListener('updatefound', () => {
    const installing = registration.installing;
    if (!installing) return;
    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed') report();
    });
  });

  let lastCheck = Date.now();
  const check = () => {
    lastCheck = Date.now();
    registration.update().catch(() => { /* offline: try again later */ });
  };
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && Date.now() - lastCheck > RECHECK_ON_SHOW_MS) check();
  });
  setInterval(check, RECHECK_EVERY_MS);
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then((registration) => registration.unregister())
      .catch((error) => console.error(error.message));
  }
}
